import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import moment from 'moment-timezone'

import Button from 'react-bootstrap/Button'

import copyIcon from '../../../../../../../assets/icons/copy.svg'
import { Spinner } from '../../../../../../LoadingContainer/Spinner'
import {
  getEventSeatDetail,
  specificGuestLookup,
} from '../../../../../../../utilities/api'
import {
  formatCurrency,
  getTimezoneDate,
} from '../../../../../../../utilities/helpers'
import { useOrderActions } from '../../../../../../../utilities/hooks/useOrderActions'
import AuthService from '../../../../../../../utilities/services/auth.service'
import { formatSeatLocationLabel } from '../../purchaserClusterUtils'
import { SeatLabelsForm } from '../Panel/SeatLabelsForm'

import './seatDetailsPanel.scss'

function displayName(identity) {
  const full = [identity?.firstName, identity?.lastName]
    .filter(Boolean)
    .join(' ')
  return full || identity?.email || 'Guest'
}

function initialsFromName(name) {
  const parts = String(name || '')
    .split(/\s+/)
    .filter(Boolean)
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase()
  }
  return String(name || '??')
    .slice(0, 2)
    .toUpperCase()
}

function statusDisplayLabel(status) {
  if (!status) return null
  const normalized = String(status).toLowerCase()
  if (normalized === 'complete' || normalized === 'paid') {
    return 'Fully paid'
  }
  return status.replace(/_/g, ' ')
}

function isPaidCompleteStatus(status) {
  const normalized = String(status || '').toLowerCase()
  return normalized === 'complete' || normalized === 'paid'
}

function formatPurchasedDisplay(raw, timezone) {
  if (!raw) return null
  const date = timezone ? getTimezoneDate(raw, timezone) : moment(raw)
  if (!date?.isValid?.()) return null
  return `${date.format('MMM D, YYYY')} • ${date.format('ddd')} • ${date.format('h:mm A')}`
}

function buildIdentityFromDetailResponse(data, leanIdentity) {
  const { order, user, purchaserSeats, ticket } = data || {}
  if (!order && !leanIdentity) return null

  return {
    ...leanIdentity,
    firstName: order?.firstName ?? user?.firstName ?? leanIdentity?.firstName,
    lastName: order?.lastName ?? user?.lastName ?? leanIdentity?.lastName,
    email: order?.email ?? user?.email ?? leanIdentity?.email,
    orderId: order?.id ?? leanIdentity?.orderId,
    orderDisplayId: order?.orderId ?? leanIdentity?.orderDisplayId,
    orderUuid: order?.uuid ?? leanIdentity?.orderUuid,
    orderStatus: order?.status ?? leanIdentity?.orderStatus,
    orderTotal: order?.total ?? leanIdentity?.orderTotal,
    orderProcessedAt: order?.processedAt ?? leanIdentity?.orderProcessedAt,
    orderCreatedAt: order?.createdAt ?? leanIdentity?.orderCreatedAt,
    onSaleStatus: ticket?.on_sale_status ?? leanIdentity?.onSaleStatus,
    listingId: ticket?.listingId ?? leanIdentity?.listingId,
    listingAskingPrice:
      ticket?.listingAskingPrice ?? leanIdentity?.listingAskingPrice,
    sectionNumber: ticket?.sectionNumber ?? leanIdentity?.sectionNumber,
    rowNumber: ticket?.rowNumber ?? leanIdentity?.rowNumber,
    seatNumber: ticket?.seatNumber ?? leanIdentity?.seatNumber,
    purchasedSeats: purchaserSeats ?? [],
  }
}

function extractSeatDetailPayload(res) {
  const body = res?.data
  if (!body || typeof body !== 'object') return null
  if (body.ticket !== undefined || body.order !== undefined) return body
  if (body.data && typeof body.data === 'object') return body.data
  return body
}

async function copyText(text) {
  if (!text) return
  try {
    await navigator.clipboard.writeText(String(text))
  } catch (_) {
    /* clipboard unavailable */
  }
}

function CopyButton({ value, label }) {
  if (!value) return null
  return (
    <button
      type="button"
      className="seat-details-panel__copy"
      aria-label={`Copy ${label}`}
      onClick={() => copyText(value)}
    >
      <img src={copyIcon} alt="" aria-hidden="true" />
    </button>
  )
}

export default function SeatDetailsPanel({
  eventUuid,
  anchorSeatId,
  seatIdentity: leanIdentity,
  timezone,
  onClose,
  seatLabels = {},
  onLabelsChange,
  canEditLabels = false,
}) {
  const orderActions = useOrderActions()
  const [actionLoading, setActionLoading] = useState(false)
  const [detailLoading, setDetailLoading] = useState(true)
  const [detailError, setDetailError] = useState(false)
  const [detailIdentity, setDetailIdentity] = useState(null)
  const leanIdentityRef = useRef(leanIdentity)
  leanIdentityRef.current = leanIdentity

  useEffect(() => {
    if (!eventUuid || anchorSeatId == null) {
      setDetailLoading(false)
      setDetailIdentity(leanIdentityRef.current ?? null)
      return undefined
    }

    const controller = new AbortController()
    const lean = leanIdentityRef.current
    setDetailLoading(true)
    setDetailError(false)
    setDetailIdentity(lean ?? null)

    getEventSeatDetail(eventUuid, anchorSeatId, controller.signal)
      .then((res) => {
        const merged = buildIdentityFromDetailResponse(
          extractSeatDetailPayload(res),
          lean
        )
        setDetailIdentity(merged ?? lean ?? null)
      })
      .catch((err) => {
        if (err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') {
          return
        }
        setDetailError(true)
        setDetailIdentity(lean ?? null)
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setDetailLoading(false)
        }
      })

    return () => controller.abort()
  }, [eventUuid, anchorSeatId])

  const seatIdentity = detailIdentity

  const name = displayName(seatIdentity)
  const email = seatIdentity?.email || ''
  const hasBuyerInfo = Boolean(
    seatIdentity?.email ||
    seatIdentity?.firstName ||
    seatIdentity?.lastName ||
    seatIdentity?.orderId
  )

  const purchaserSeats = useMemo(() => {
    if (Array.isArray(seatIdentity?.purchasedSeats)) {
      return seatIdentity.purchasedSeats
    }
    return []
  }, [seatIdentity?.purchasedSeats])

  const anchorLocation = useMemo(() => {
    const anchor =
      purchaserSeats.find((s) => s.seatId === anchorSeatId) || seatIdentity
    return formatSeatLocationLabel(anchor)
  }, [purchaserSeats, anchorSeatId, seatIdentity])

  const purchased = formatPurchasedDisplay(
    seatIdentity?.orderProcessedAt || seatIdentity?.orderCreatedAt,
    timezone
  )
  const statusLabel = statusDisplayLabel(seatIdentity?.orderStatus)
  const isListedForResale = seatIdentity?.onSaleStatus === 'resaleAvailable'
  const paidComplete = isPaidCompleteStatus(seatIdentity?.orderStatus)
  const totalDisplay =
    seatIdentity?.orderTotal != null
      ? formatCurrency(Number(seatIdentity.orderTotal))
      : null

  const organizationUUID = AuthService.getOrg()?.[0]?.uuid

  const orderForActions = useMemo(() => {
    if (!seatIdentity?.orderId && !email) return null
    return {
      id: seatIdentity?.orderId,
      uuid: seatIdentity?.orderUuid,
      orderId: seatIdentity?.orderDisplayId,
      email,
      firstName: seatIdentity?.firstName,
      lastName: seatIdentity?.lastName,
      status: seatIdentity?.orderStatus,
      total: seatIdentity?.orderTotal,
      processedAt: seatIdentity?.orderProcessedAt,
      createdAt: seatIdentity?.orderCreatedAt,
      organizationUUID,
    }
  }, [seatIdentity, email, organizationUUID])

  const resolveFullOrder = useCallback(async () => {
    if (!seatIdentity?.orderId || !organizationUUID) return orderForActions
    try {
      const res = await specificGuestLookup({
        orderId: seatIdentity.orderId,
        organizationUUID,
      })
      return {
        ...orderForActions,
        ...res.data,
        id: seatIdentity.orderId,
        organizationUUID,
      }
    } catch {
      return orderForActions
    }
  }, [seatIdentity?.orderId, organizationUUID, orderForActions])

  const handleVisitProfile = async () => {
    if (!orderForActions) return
    setActionLoading(true)
    try {
      const fullOrder = await resolveFullOrder()
      orderActions.openViewProfile(fullOrder || orderForActions)
    } finally {
      setActionLoading(false)
    }
  }

  const handleViewFullOrder = async () => {
    if (!orderForActions) return
    setActionLoading(true)
    try {
      const fullOrder = await resolveFullOrder()
      orderActions.openInvoiceHtml(fullOrder || orderForActions)
    } finally {
      setActionLoading(false)
    }
  }

  const canUseActions = Boolean(orderForActions?.id && organizationUUID)

  return (
    <div className="seat-details-panel-wrap">
      <div className="seat-details-panel">
        <div className="seat-details-panel__header">
          <h2 className="seat-details-panel__title">Seat details</h2>
          <button
            type="button"
            className="seat-details-panel__close"
            aria-label="Close seat details"
            onClick={onClose}
          >
            ×
          </button>
        </div>

        <div className="seat-details-panel__body">
          {detailLoading && !hasBuyerInfo && (
            <div className="seat-details-panel__loading">
              <Spinner variant="dark" />
            </div>
          )}

          {!detailLoading && detailError && (
            <p className="seat-details-panel__error text-muted mb-3">
              Could not load full order details. Showing available seat info.
            </p>
          )}

          {!detailLoading && !hasBuyerInfo && (
            <>
              {isListedForResale && (
                <p className="fw-semi-bold mb-2">Listed for resale</p>
              )}
              <p className="text-muted mb-0">No order found for this seat.</p>
            </>
          )}

          {canEditLabels && anchorSeatId != null && (
            <SeatLabelsForm
              eventUuid={eventUuid}
              selectedSeatIds={{ seats: { details: [anchorSeatId] } }}
              seatLabels={seatLabels}
              onLabelsChange={onLabelsChange}
            />
          )}

          {hasBuyerInfo && (
            <>
              <div className="seat-details-panel__buyer">
                <div className="seat-details-panel__avatar" aria-hidden="true">
                  {initialsFromName(name)}
                </div>
                <div>
                  <p className="seat-details-panel__name">{name}</p>
                  {anchorLocation && (
                    <p className="seat-details-panel__location">
                      {anchorLocation}
                    </p>
                  )}
                </div>
              </div>

              <div className="seat-details-panel__meta">
                {seatIdentity?.orderDisplayId && (
                  <div className="seat-details-panel__row">
                    <span className="seat-details-panel__label">Order ID</span>
                    <span className="seat-details-panel__value-row">
                      <span className="seat-details-panel__value">
                        {seatIdentity.orderDisplayId}
                      </span>
                      <CopyButton
                        value={seatIdentity.orderDisplayId}
                        label="order ID"
                      />
                    </span>
                  </div>
                )}
                {email && (
                  <div className="seat-details-panel__row">
                    <span className="seat-details-panel__label">Email</span>
                    <span className="seat-details-panel__value-row">
                      <span className="seat-details-panel__value">{email}</span>
                      <CopyButton value={email} label="email" />
                    </span>
                  </div>
                )}
                {purchased && (
                  <div className="seat-details-panel__row">
                    <span className="seat-details-panel__label">Purchased</span>
                    <span className="seat-details-panel__value">
                      {purchased}
                    </span>
                  </div>
                )}
                {totalDisplay && (
                  <div className="seat-details-panel__row">
                    <span className="seat-details-panel__label">
                      Order Total
                    </span>
                    <span className="seat-details-panel__value">
                      {totalDisplay}
                    </span>
                  </div>
                )}
                {statusLabel && (
                  <div className="seat-details-panel__row">
                    <span className="seat-details-panel__label">Status</span>
                    <span
                      className={
                        paidComplete
                          ? 'seat-details-panel__value seat-details-panel__status--paid'
                          : 'seat-details-panel__value'
                      }
                    >
                      {statusLabel}
                    </span>
                  </div>
                )}
                {isListedForResale && (
                  <div className="seat-details-panel__row">
                    <span className="seat-details-panel__label">
                      Inventory status
                    </span>
                    <span className="seat-details-panel__value">
                      Listed for resale
                    </span>
                  </div>
                )}
                {isListedForResale &&
                  seatIdentity?.listingAskingPrice != null && (
                    <div className="seat-details-panel__row">
                      <span className="seat-details-panel__label">
                        Resale asking price
                      </span>
                      <span className="seat-details-panel__value">
                        {formatCurrency(
                          Number(seatIdentity.listingAskingPrice)
                        )}
                      </span>
                    </div>
                  )}
              </div>

              {purchaserSeats.length > 0 && (
                <div className="seat-details-panel__seats">
                  <p className="seat-details-panel__seats-title">
                    Seats purchased ({purchaserSeats.length})
                  </p>
                  <ul className="seat-details-panel__seats-list">
                    {purchaserSeats.map((seat) => (
                      <li key={seat.seatId}>{formatSeatLocationLabel(seat)}</li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>

        {hasBuyerInfo && !detailLoading && (
          <div className="seat-details-panel__footer">
            <Button
              type="button"
              variant="default"
              className="seat-details-panel__action"
              disabled={!canUseActions || actionLoading}
              onClick={handleVisitProfile}
            >
              Visit profile
            </Button>
            <Button
              type="button"
              variant="default"
              className="seat-details-panel__action"
              disabled={!canUseActions || actionLoading}
              onClick={handleViewFullOrder}
            >
              View full order
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}
